import { create } from 'zustand';

export type SessionUser = {
  id: string;
  email: string;
  displayName: string;
  tenantId?: string;
  roles: string[];
  permissions?: string[];
  /** How the most recent sign-in came in; an SSO user has no portal password to change. */
  authMethod?: 'SSO' | 'PASSWORD';
};

/**
 * Three states, not a boolean: `unknown` is what a boolean cannot express, so a guard would
 * redirect to /auth/login before the boot refresh resolves (the login flash). `error` keeps a
 * failing backend from becoming a redirect loop: valid tokens but no profile is terminal and must
 * surface, since retrying via the Hosted UI would repeat forever with nothing reporting it.
 */
export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous' | 'error';

type SessionState = {
  status: SessionStatus;
  user: SessionUser | null;
  /** Why the session is in `error`. Shown to the user; never contains token material. */
  error: string | null;
  setAuthenticated: (user: SessionUser) => void;
  setAnonymous: () => void;
  setError: (message: string) => void;
};

export const useSessionStore = create<SessionState>()((set) => ({
  status: 'unknown',
  user: null,
  error: null,
  setAuthenticated: (user) => set({ status: 'authenticated', user, error: null }),
  setAnonymous: () => set({ status: 'anonymous', user: null, error: null }),
  setError: (message) => set({ status: 'error', user: null, error: message }),
}));

// Module-scoped, never in the store or any storage API: Zustand devtools/persist would otherwise
// surface it, and storage would make it readable by any injected script. Holds the OIDC
// access_token, not the id_token — the backend authorizes on the access token, so real call sites
// read `user.access_token`; mock mode writes an opaque stand-in instead.
let bearerToken: string | null = null;

export function getBearerToken(): string | null {
  return bearerToken;
}

export function setBearerToken(token: string | null): void {
  bearerToken = token;
}
